import React from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const SearchAndFilter = ({
  searchTerm,
  setSearchTerm,
  statusFilter,
  setStatusFilter,
  dateRange,
  setDateRange,
  sortBy,
  setSortBy,
  sortOrder,
  setSortOrder
}) => {
  const statusOptions = [
    { value: 'all', label: 'All Statuses', description: 'Show transactions with any status' },
    { value: 'confirmed', label: 'Confirmed', description: 'Successfully completed transactions' },
    { value: 'pending', label: 'Pending', description: 'Transactions awaiting confirmation' },
    { value: 'failed', label: 'Failed', description: 'Transactions that failed' }
  ];

  const dateOptions = [
    { value: 'all', label: 'All Time', description: 'Show all transactions' },
    { value: '24h', label: 'Last 24 Hours', description: 'Transactions from the last day' },
    { value: '7d', label: 'Last 7 Days', description: 'Transactions from the last week' },
    { value: '30d', label: 'Last 30 Days', description: 'Transactions from the last month' }
  ];

  const sortOptions = [
    { value: 'timestamp', label: 'Date Created', description: 'Sort by creation date' },
    { value: 'status', label: 'Status', description: 'Sort by transaction status' },
    { value: 'gasUsed', label: 'Gas Used', description: 'Sort by gas consumption' },
    { value: 'originalUrl', label: 'URL', description: 'Sort by original URL' }
  ];

  const clearAllFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setDateRange('all');
    setSortBy('timestamp');
    setSortOrder('desc');
  };

  const hasActiveFilters = searchTerm || statusFilter !== 'all' || dateRange !== 'all';

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="space-y-4">
        {/* Search Bar */}
        <div className="relative">
          <Icon name="Search" size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search by URL, transaction hash, or title..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="pl-10 pr-10"
          />
          {searchTerm && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6"
              onClick={() => setSearchTerm('')}
            >
              <Icon name="X" size={14} />
            </Button>
          )}
        </div>

        {/* Filters Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Status Filter */}
          <Select
            label="Status"
            value={statusFilter}
            onChange={setStatusFilter}
            options={statusOptions}
          />

          {/* Date Range Filter */}
          <Select
            label="Date Range"
            value={dateRange}
            onChange={setDateRange}
            options={dateOptions}
          />

          {/* Sort By */}
          <Select
            label="Sort By"
            value={sortBy}
            onChange={setSortBy}
            options={sortOptions}
          />

          {/* Sort Order */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Sort Order</label>
            <div className="flex rounded-md border border-input">
              <Button
                variant={sortOrder === 'desc' ? 'default' : 'ghost'}
                size="sm"
                className="flex-1 rounded-r-none"
                iconName="ArrowDown"
                onClick={() => setSortOrder('desc')}
              >
                Desc
              </Button>
              <Button
                variant={sortOrder === 'asc' ? 'default' : 'ghost'}
                size="sm"
                className="flex-1 rounded-l-none"
                iconName="ArrowUp"
                onClick={() => setSortOrder('asc')}
              >
                Asc
              </Button>
            </div>
          </div>
        </div>

        {/* Active Filters and Clear */}
        {hasActiveFilters && (
          <div className="flex items-center justify-between pt-4 border-t border-border">
            <div className="flex items-center space-x-2">
              <Icon name="Filter" size={16} className="text-primary" />
              <span className="text-sm font-medium text-foreground">Active Filters:</span>
              <div className="flex items-center space-x-2">
                {searchTerm && (
                  <span className="px-2 py-1 bg-primary text-primary-foreground text-xs rounded-full">
                    Search: "{searchTerm?.length > 20 ? searchTerm?.slice(0, 20) + '...' : searchTerm}"
                  </span>
                )}
                {statusFilter !== 'all' && (
                  <span className="px-2 py-1 bg-primary text-primary-foreground text-xs rounded-full">
                    Status: {statusFilter}
                  </span>
                )}
                {dateRange !== 'all' && (
                  <span className="px-2 py-1 bg-primary text-primary-foreground text-xs rounded-full">
                    Date: {dateOptions?.find(opt => opt?.value === dateRange)?.label}
                  </span>
                )}
              </div>
            </div>
            
            <Button
              variant="outline"
              size="sm"
              iconName="X"
              onClick={clearAllFilters}
            >
              Clear All
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchAndFilter;